import {useEffect, useRef, useState} from 'react'
import {useQueryClient} from '@tanstack/react-query'
import {useAppConfig, pickupArrivalCell} from '@/hooks/useAppConfig'
import {tripService} from '@/services/tripService'
import {tripPickupPoint} from '@/features/fromAirportArrival/arrivalEligibility'
import {metresBetween} from '@/utils/polyline'
import {MAX_ARRIVAL_ACCURACY_METERS} from '@/hooks/useArrivedAtPickupPoint'

/**
 * "I'M AT THE PICKUP POINT" — the passenger app's half of the backend's reached-pickup signal.
 *
 * The APP owns the geofence: while the trip is in the pre-boarding window it watches the
 * device against `trip.origin`, and the FIRST time it is inside the admin-set radius it calls
 * `POST /passengers/trips/{trip}/reached-pickup` once. The server stamps the trip, tells the
 * driver ("Passenger at the pickup point") and ignores repeats. No button — this is automatic.
 *
 * ⚠️ WHETHER, AND HOW WIDE, COME FROM `/config/app` (`pickup_arrival.passenger.<direction>`),
 * never from a number here. Admin Settings → Pickup Arrival turns each direction on or off and
 * sets `radius_m`. EVERY direction the config switches on is watched — from_airport, to_airport
 * and others alike (it used to be from-airport only in code; removed 2026-10-12). See `pickupArrivalCell`.
 *
 * ⚠️ THE TRIP'S OWN STAMP ENDS IT. `trip.reached_pickup.passenger_at` set means this (or an
 * earlier install) already reported — survives reloads, so a refresh never re-fires.
 */

const TAG = 'color:#0f9d76;font-weight:bold'
// Always printed: at most once per trip, and the console must say whether the auto report went out.
const log = (...args) => console.log('%c[reached-pickup]', TAG, ...args)

/** The pre-boarding window the backend accepts a report in. */
const WATCH_STATUSES = ['Assigned', 'Accepted', 'EnRoute']

/**
 * INSIDE FOR THIS LONG, NOT "TWO FIXES IN A ROW". One browser fix is a coin toss and the
 * report is sent once, so it has to stay inside — but counting fixes never finished for a
 * passenger standing still: `useMyLocation` does not republish an unchanged coordinate, so a
 * stationary device (or a location override) produced ONE fix and the count stuck at 1.
 * A dwell timer works whether the position updates or not.
 */
const DWELL_MS = 3000
/** After a refusal that may change (`outside_pickup_radius`, network), try again this soon. */
const RETRY_MS = 10000

/**
 * Which trip to watch, and against what — the FIRST of `trips` (full trip details) that is in
 * the pre-boarding window, switched on for its direction, and not yet stamped.
 * Read BEFORE the position exists, because it is what switches the location watch on.
 */
export function usePickupGeofenceTarget(trips) {
    const {data: config} = useAppConfig()
    for (const trip of trips || []) {
        if (!trip || !WATCH_STATUSES.includes(trip.status) || trip.reached_pickup?.passenger_at) continue
        const {enabled, radiusM} = pickupArrivalCell(config, 'passenger', trip.trip_direction)
        const pickup = tripPickupPoint(trip)
        if (enabled && pickup) return {trip, wanted: true, radiusM, pickup}
    }
    return {trip: null, wanted: false, radiusM: null, pickup: null}
}

/**
 * @param {object|null} trip  the FULL trip (carries `reached_pickup`)
 * @param {{wanted: boolean, radiusM: number|null, pickup: {lat: number, lng: number}|null}} target
 * @param {{lat: number, lng: number, accuracy_m?: number|null}|null} position
 * @returns {string|number|null} the trip id the report just went out for (drives the modal)
 */
export function useReachedPickupGeofence(trip, target, position) {
    const queryClient = useQueryClient()
    const [reportedTripId, setReportedTripId] = useState(null)
    const inFlight = useRef(false)
    const doneFor = useRef(new Set())
    const timer = useRef(null)

    const tripId = trip?.id ?? null
    const {wanted, radiusM, pickup} = target
    /* Primitives for the effect's deps: `pickup` is a fresh object every render, and an effect
       keyed on it would re-arm the timer on every render. */
    const pickupLat = pickup?.lat
    const pickupLng = pickup?.lng
    const lat = position?.lat
    const lng = position?.lng
    const accuracy = position?.accuracy_m

    /* The latest of everything, for the timer to re-check when it fires. */
    const latest = useRef({})
    latest.current = {wanted, tripId, radiusM, pickupLat, pickupLng, lat, lng, accuracy}

    /** Inside the radius with a fix precise enough to say so — read from `latest`. */
    const isInside = () => {
        const v = latest.current
        if (!v.wanted || v.tripId == null || doneFor.current.has(String(v.tripId))) return false
        if (![v.lat, v.lng, v.pickupLat, v.pickupLng].every(Number.isFinite)) return false
        /* A fix whose own error bar is wider than the radius cannot answer the question the
           radius asks (a laptop's wifi fix routinely reports "here" from kilometres away). */
        if (Number.isFinite(v.accuracy) && v.accuracy > Math.max(MAX_ARRIVAL_ACCURACY_METERS, v.radiusM)) return false
        return metresBetween({lat: v.lat, lng: v.lng}, {lat: v.pickupLat, lng: v.pickupLng}) <= v.radiusM
    }

    const fire = () => {
        timer.current = null
        if (inFlight.current || !isInside()) return
        const {tripId: id, radiusM: radius, lat: fixLat, lng: fixLng, accuracy: fixAccuracy} = latest.current

        inFlight.current = true
        log(`trip ${id}: passenger inside ${radius}m of pickup — reporting`)
        tripService.reportReachedPickup(id, {lat: fixLat, lng: fixLng, accuracy_m: fixAccuracy}).then((result) => {
            log(`trip ${id}:`, result.success ? 'recorded' : 'not recorded', result.success ? result.data?.reached_pickup : (result.raw?.key || result.error))
            if (result.success) {
                doneFor.current.add(String(id))
                /* `already_reported`: an earlier install got there first — nothing changed, so
                   there is nothing new to tell the passenger. */
                if (!result.data?.reached_pickup?.already_reported) setReportedTripId(id)
                queryClient.invalidateQueries({queryKey: ['trip', id]})
                queryClient.invalidateQueries({queryKey: ['trips']})
                return
            }
            /* Not in the window any more: stop for good. Anything else (server-side radius check
               disagrees, network): try again shortly if still inside — a passenger standing
               still produces no new fix to retry on. */
            if (result.raw?.key === 'trip_not_active') doneFor.current.add(String(id))
            else if (!timer.current) timer.current = setTimeout(fire, RETRY_MS)
        }).finally(() => { inFlight.current = false })
    }

    useEffect(() => {
        if (isInside()) {
            if (!timer.current) timer.current = setTimeout(fire, DWELL_MS)
        } else if (timer.current) {
            clearTimeout(timer.current)
            timer.current = null
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [wanted, tripId, lat, lng, accuracy, radiusM, pickupLat, pickupLng])

    useEffect(() => () => { if (timer.current) clearTimeout(timer.current) }, [])

    return reportedTripId
}

export default useReachedPickupGeofence
